/** Cells for the bookings grid's expansion columns (D5): Guides, Start, Route, Guide app
 *  and Chat. Each reads a row property the list returns only when the grid asks for its
 *  expansion (`?expand=`, driven by the visible columns), so an absent property renders
 *  "—". Every status is an icon AND words; the evidence is in the tooltip. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LogoutIcon from '@mui/icons-material/Logout';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { Box, Tooltip, Typography, type SvgIconProps } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';

import type { BookingListItem, BookingPersonRef, BookingPlace } from '@/api/bookings';

import { chatStatus, guideAppStatus, type TripStatus, type TripStatusKind } from './tripStatus';

/** "Anna B." — first name and last initial; a lone name as is; else `User #id`. */
export function shortName(p: BookingPersonRef): string {
  const first = p.first_name?.trim();
  const last = p.last_name?.trim();
  if (first && last) return `${first} ${last[0]}.`;
  return first || last || `User #${p.id}`;
}

function fullName(p: BookingPersonRef): string {
  return [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || `User #${p.id}`;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

function Muted({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {children}
    </Box>
  );
}

function Lines({ lines }: { lines: string[] }) {
  return (
    <Box>
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </Box>
  );
}

/** One or two lines in a cell, the second muted (caption); `evidence` lines, when any,
 *  are its tooltip. */
function TwoLines({ main, sub, evidence = [] }: { main: ReactNode; sub?: ReactNode; evidence?: string[] }) {
  const body = (
    <Box sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{main}</Box>
      {sub && (
        <Typography component="div" variant="caption" color="text.secondary" noWrap>
          {sub}
        </Typography>
      )}
    </Box>
  );
  if (evidence.length === 0) return body;
  return (
    <Tooltip title={<Lines lines={evidence} />} placement="top" arrow>
      {body}
    </Tooltip>
  );
}

// ---- Guides -----------------------------------------------------------------

/** Who works the departure. A guest's row names its guides and staff; a staff row says
 *  how many guests the departure has and who they work with. The booking's own
 *  person-naming tags (guide / coordinator / van / bus) are the muted second line. */
export function guidesText(row: BookingListItem): string {
  const g = row.guides;
  if (!g) return '';
  const names = g.staff.map((s) => shortName(s.user)).join(', ');
  const more = g.staff_more ? ` +${g.staff_more}` : '';
  const isStaff = row.participant_kind === 'guide' || row.participant_kind === 'staff';
  if (isStaff) {
    const guests = g.guest_count !== null ? plural(g.guest_count, 'guest') : null;
    const withWhom = names ? `with ${names}${more}` : null;
    return [guests, withWhom].filter(Boolean).join(' · ');
  }
  return names ? `${names}${more}` : '';
}

export function GuidesCell({ row }: { row: BookingListItem }) {
  const g = row.guides;
  if (!g) return <>—</>;
  const main = guidesText(row) || <Muted>No staff on the departure</Muted>;
  const assigned = g.assigned
    .map((a) => `${a.category?.name ?? 'Assigned'}: ${a.tag.name}`)
    .join(' · ');
  const evidence = [
    ...g.staff.map((s) => `${fullName(s.user)} — ${s.role.name} (${s.role.kind})`),
    ...(g.staff_more ? [`+${g.staff_more} more staff`] : []),
    ...g.assigned.map(
      (a) => `${a.category?.name ?? 'Assigned'} tag “${a.tag.name}” → ${fullName(a.user)}`,
    ),
  ];
  return <TwoLines main={main} sub={assigned || undefined} evidence={evidence} />;
}

// ---- Where ------------------------------------------------------------------

export const PLACE_SOURCE_TEXT: Record<BookingPlace['source'], string> = {
  pickup: 'Pickup',
  meeting_point: 'Meeting point',
  tour_meeting_point: 'Tour meeting point',
  first_activity: 'First activity',
};

export function startPlaceText(row: BookingListItem): string {
  const s = row.where?.start;
  return s ? (s.name ?? s.address ?? '') : '';
}

/** Where the booking starts: the place, and which source named it. */
export function StartPlaceCell({ row }: { row: BookingListItem }) {
  const w = row.where;
  if (!w) return <>—</>;
  const s = w.start;
  if (!s) return <Muted>No start place</Muted>;
  const evidence = [
    ...(s.name && s.address ? [s.address] : []),
    ...(s.lat !== null && s.lng !== null ? [`${s.lat}, ${s.lng}`] : []),
  ];
  return <TwoLines main={startPlaceText(row)} sub={PLACE_SOURCE_TEXT[s.source]} evidence={evidence} />;
}

export function routeText(row: BookingListItem): string {
  const w = row.where;
  if (!w || w.route.length === 0) return '';
  return w.route.map((r) => r.name).join(' → ') + (w.route_more ? ` +${w.route_more}` : '');
}

/** "bokun" → "Bokun", for "No route from Bokun" (and the user record's trip rows). */
export const integrationName = (code: string) => code.charAt(0).toUpperCase() + code.slice(1);

/** Where the trip goes: the itinerary's stops, else the tour's country / destination tags,
 *  else a muted "No route" that names the integration that never sends one. */
export function RouteCell({ row }: { row: BookingListItem }) {
  const w = row.where;
  if (!w) return <>—</>;
  if (w.route.length === 0) {
    return (
      <Muted>No route{row.integration ? ` from ${integrationName(row.integration)}` : ''}</Muted>
    );
  }
  const countries = [...new Set(w.route.map((r) => r.country).filter((c): c is string => !!c))];
  const sub = w.route_source === 'tour_tags' ? 'From the tour’s tags' : undefined;
  return (
    <TwoLines main={routeText(row)} sub={sub} evidence={countries.length ? [countries.join(', ')] : []} />
  );
}

// ---- Guide app / Chat ---------------------------------------------------------

const STATUS_ICON: Record<TripStatusKind, ComponentType<SvgIconProps>> = {
  in: FiberManualRecordIcon,
  upcoming: RadioButtonUncheckedIcon,
  left: LogoutIcon,
  off: RemoveCircleOutlineIcon,
  stale: SyncProblemIcon,
  alert: ErrorOutlineIcon,
};

/** Navy for the states the person is in or will be in; amber only for the pending cache
 *  refresh; Signal Red only for "should be there and is not". */
const STATUS_ICON_COLOR: Record<TripStatusKind, string> = {
  in: 'primary.main',
  upcoming: 'primary.main',
  left: 'text.secondary',
  off: 'text.secondary',
  stale: 'warning.main',
  alert: 'error.main',
};

/** One status: an icon and its words, the evidence in the tooltip. */
export function TripStatusLine({ status }: { status: TripStatus }) {
  const Icon = STATUS_ICON[status.kind];
  const muted = status.kind === 'left' || status.kind === 'off';
  return (
    <Tooltip title={<Lines lines={status.evidence} />} placement="top" arrow>
      <Box
        component="span"
        data-status={status.kind}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          maxWidth: '100%',
          color: muted ? 'text.secondary' : 'text.primary',
        }}
      >
        <Icon aria-hidden sx={{ fontSize: 14, flexShrink: 0, color: STATUS_ICON_COLOR[status.kind] }} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {status.text}
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Is the booking in front of the guide in the app? Staff rows only — guests and observers
 *  have no app window (null), so "—". */
export function GuideAppCell({ row }: { row: BookingListItem }) {
  if (!row.staff_visibility) return <>—</>;
  return <TripStatusLine status={guideAppStatus(row.staff_visibility, row.timezone)} />;
}

/** Is the person in the trip's chat? Every row (guests too). */
export function ChatCell({ row }: { row: BookingListItem }) {
  if (!row.chat) return <>—</>;
  return <TripStatusLine status={chatStatus(row.chat, row.timezone)} />;
}
